import { useState, useEffect } from 'react';
import { api } from './utils/api';
import { useAuth } from './hooks/useAuth';
import Login from './pages/Login';
import Minerados from './pages/Minerados';
import Insights from './pages/Insights';
import Monitoramento from './pages/Monitoramento';
import BotConfig from './pages/BotConfig';
import Logs from './pages/Logs';
import Dashboard from './pages/Dashboard';
import CloneSite from './pages/CloneSite';
import ManualMining from './pages/ManualMining';
import VslDownloader from './pages/VslDownloader';
import AdLibraryDownloader from './pages/AdLibraryDownloader';
import AdminUsers from './pages/AdminUsers';
import AcceptInvitation from './pages/AcceptInvitation';
import ProductTests from './pages/ProductTests';

const tabs = [
  { key: 'Dashboard', label: 'Dashboard', icon: '◉' },
  { key: 'Minerados', label: 'Minerados', icon: '◎' },
  { key: 'Monitoramento', label: 'Monitoramento', icon: '◈' },
  { key: 'ManualMining', label: 'Mineração Manual', icon: '✎' },
  { key: 'ProductTests', label: 'Produtos em Teste', icon: '⊞' },
  { key: 'VslDownloader', label: 'Baixar VSL', icon: '▼' },
  { key: 'AdLibraryDownloader', label: 'Baixar anúncios', icon: '▥' },
  { key: 'CloneSite', label: 'Clone Site', icon: '▦' },
  { key: 'Insights', label: 'Insights', icon: '◇' },
  { key: 'Bot', label: 'Bot', icon: '⚙' },
  { key: 'Logs', label: 'Logs', icon: '▤' },
  { key: 'AdminUsers', label: 'Usuarios', icon: '⚑', adminOnly: true },
];

const slugMap = {};
tabs.forEach(t => { slugMap[t.key.toLowerCase()] = t.key; });

function getTabFromHash() {
  const hash = window.location.hash.replace('#', '').toLowerCase();
  return slugMap[hash] || 'Dashboard';
}

export default function App() {
  const { isAuthenticated, loading: authLoading, user, logout } = useAuth();
  const [tab, setTab] = useState(getTabFromHash);
  const [session, setSession] = useState(null);
  const [collapsed, setCollapsed] = useState(false);

  const changeTab = (t) => {
    setTab(t);
    window.location.hash = t.toLowerCase();
  };

  useEffect(() => {
    const onHash = () => setTab(getTabFromHash());
    window.addEventListener('hashchange', onHash);
    return () => window.removeEventListener('hashchange', onHash);
  }, []);

  useEffect(() => {
    if (!isAuthenticated) return;
    const poll = () => api.getActiveSession().then(d => setSession(d.session)).catch(() => {});
    poll();
    const iv = setInterval(poll, 10000);
    return () => clearInterval(iv);
  }, [isAuthenticated]);

  if (authLoading) return <div style={{ minHeight: '100vh', background: 'var(--bg)' }} />;

  // Rota publica: aceitar convite (bypass auth). Hash: #/aceitar-convite?token=X
  const rawHash = typeof window !== 'undefined' ? window.location.hash : '';
  if (rawHash.startsWith('#/aceitar-convite') || rawHash.startsWith('#aceitar-convite')) {
    return <AcceptInvitation />;
  }

  if (!isAuthenticated) return <Login />;

  const sidebarW = collapsed ? 60 : 220;

  return (
    <div style={{ display: 'flex', minHeight: '100vh' }}>
      {/* Sidebar */}
      <aside style={{ ...s.sidebar, width: sidebarW }}>
        <div style={s.logoArea}>
          {collapsed ? (
            <div style={s.logoIcon}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="8" height="16" rx="2" fill="var(--accent)" />
                <rect x="12" y="8" width="5" height="12" rx="1" fill="var(--accent)" opacity="0.5" />
                <rect x="19" y="12" width="3" height="8" rx="1" fill="var(--accent)" opacity="0.3" />
              </svg>
            </div>
          ) : (
            <div style={s.logoFull}>
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <rect x="2" y="4" width="8" height="16" rx="2" fill="var(--accent)" />
                <rect x="12" y="8" width="5" height="12" rx="1" fill="var(--accent)" opacity="0.5" />
                <rect x="19" y="12" width="3" height="8" rx="1" fill="var(--accent)" opacity="0.3" />
              </svg>
              <span style={s.logoText}><span style={s.logoBold}>AD</span>INTEL</span>
            </div>
          )}
          <button style={s.collapseBtn} onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? '▸' : '◂'}
          </button>
        </div>

        <nav style={s.nav}>
          {tabs.filter(t => !t.adminOnly || user?.is_admin).map(t => (
            <button key={t.key} onClick={() => changeTab(t.key)}
              style={{ ...s.navItem, ...(tab === t.key ? s.navActive : {}) }}>
              <span style={s.navIcon}>{t.icon}</span>
              {!collapsed && <span style={s.navLabel}>{t.label}</span>}
            </button>
          ))}
        </nav>

        <div style={s.sidebarFooter}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1 }}>
            <div style={s.statusDot(session)} />
            {!collapsed && <span style={s.statusText}>{session ? 'Minerando' : 'Offline'}</span>}
          </div>
          {!collapsed && (
            <button style={s.logoutBtn} onClick={logout} title="Sair">
              {user?.name?.charAt(0)?.toUpperCase() || '?'}
            </button>
          )}
        </div>
      </aside>

      {/* Content */}
      <main style={{ ...s.main, marginLeft: sidebarW }}>
        {tab === 'Dashboard' && <Dashboard />}
        {tab === 'Minerados' && <Minerados />}
        {tab === 'Insights' && <Insights />}
        {tab === 'Monitoramento' && <Monitoramento />}
        {tab === 'CloneSite' && <CloneSite />}
        {tab === 'VslDownloader' && <VslDownloader />}
        {tab === 'AdLibraryDownloader' && <AdLibraryDownloader />}
        {tab === 'ProductTests' && <ProductTests />}
        {tab === 'AdminUsers' && <AdminUsers />}
        {tab === 'ManualMining' && <ManualMining />}
        {tab === 'Bot' && <BotConfig />}
        {tab === 'Logs' && <Logs />}
      </main>
    </div>
  );
}

const s = {
  sidebar: {
    position: 'fixed', top: 0, left: 0, bottom: 0,
    background: 'var(--surface)', borderRight: '1px solid var(--border)',
    display: 'flex', flexDirection: 'column',
    transition: 'width 0.2s ease',
    zIndex: 100, overflow: 'hidden',
  },
  logoArea: {
    padding: '14px 12px 12px', display: 'flex', alignItems: 'center',
    justifyContent: 'space-between', borderBottom: '1px solid var(--border)',
    minHeight: 48,
  },
  logoIcon: { display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoFull: { display: 'flex', alignItems: 'center', gap: 8 },
  logoText: { fontSize: 13, fontWeight: 400, color: 'var(--text2)', letterSpacing: 2 },
  logoBold: { fontWeight: 800, color: 'var(--text)' },
  collapseBtn: {
    background: 'none', border: 'none', color: 'var(--text3)', fontSize: 14,
    padding: '2px 6px', borderRadius: 4,
  },
  nav: {
    flex: 1, display: 'flex', flexDirection: 'column', gap: 2,
    padding: '12px 8px',
  },
  navItem: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '8px 12px', border: 'none', borderRadius: 6,
    background: 'transparent', color: 'var(--text2)',
    fontSize: 13, fontWeight: 500, fontFamily: 'inherit',
    textAlign: 'left', transition: 'all 0.15s',
    whiteSpace: 'nowrap',
  },
  navActive: {
    background: 'var(--surface3)', color: 'var(--text)',
    fontWeight: 600,
  },
  navIcon: { fontSize: 16, width: 20, textAlign: 'center', flexShrink: 0 },
  navLabel: { overflow: 'hidden', textOverflow: 'ellipsis' },
  sidebarFooter: {
    padding: '12px 16px', borderTop: '1px solid var(--border)',
    display: 'flex', alignItems: 'center', gap: 8,
  },
  statusDot: (session) => ({
    width: 8, height: 8, borderRadius: '50%', flexShrink: 0,
    background: session ? 'var(--accent-g)' : 'var(--text3)',
    boxShadow: session ? '0 0 6px var(--accent-g)' : 'none',
  }),
  statusText: { fontSize: 11, color: 'var(--text3)' },
  logoutBtn: { width: 26, height: 26, borderRadius: '50%', border: '1px solid var(--border)', background: 'var(--surface2)', color: 'var(--text3)', fontSize: 11, fontWeight: 600, fontFamily: 'inherit', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  main: {
    flex: 1, minWidth: 0, minHeight: '100vh',
    transition: 'margin-left 0.2s ease',
  },
};
